import React from "react";
import { RagBadge, ConfBadge, TrendBadge, Panel, Kicker, DataTable } from "../ciWidgets";

export const ScorecardTab = ({ report }) => {
  const dims = Object.values(report.dimensions || {});
  const overall = report.overall || {};

  return (
    <div className="space-y-6" data-testid="ci-tab-scorecard">
      <Panel className="p-7">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <Kicker>Enterprise value scorecard</Kicker>
          <div className="text-[12px] text-white/50">
            Overall = Σ (score × weight) ÷ Σ (weights) · all ten dimensions scored · fully reproducible
          </div>
        </div>
        <DataTable
          testId="ci-scorecard-table"
          headers={["Dimension", "Weight", "Score", "Weighted", "Trend", "Peer %ile", "RAG", "Confidence", "Evidence", "Key driver", "Critical issue", "Priority action"]}
          rows={dims.map((d) => [
            <span className="font-medium text-white">{d.name}</span>,
            <span className="tabular-nums">{d.weight}%</span>,
            <span className="font-serif text-lg tabular-nums text-white">{d.score ?? "—"}</span>,
            <span className="tabular-nums">{d.weighted ?? "—"}</span>,
            <TrendBadge trend={d.trend} />,
            <span className="tabular-nums">{d.peer_percentile ?? "—"}</span>,
            <RagBadge rag={d.rag} />,
            <ConfBadge level={d.confidence} />,
            <span className="tabular-nums">
              {d.evidence_coverage}%
              <span className="block text-[10px] text-white/40">
                {d.metrics_scored ?? "—"}/{d.metrics_total ?? "—"} metrics
              </span>
            </span>,
            <span className="text-[12px]">{d.key_driver}</span>,
            <span className="text-[12px]">{d.critical_issue}</span>,
            <span className="text-[12px] text-teal-200">{d.priority_action}</span>,
          ])}
        />
        <div className="mt-5 border-t border-white/10 pt-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-[13px]">
          <span className="text-white/60">
            Overall score:&nbsp;
            <span className="font-serif text-2xl text-white tabular-nums" data-testid="ci-overall-score-value">{overall.score_display ?? overall.score ?? "—"}</span>
          </span>
          <RagBadge rag={overall.rag} />
          <span className="text-white/50">Evidence quality {overall.evidence_quality_score ?? overall.evidence_coverage}%</span>
          <span className="text-white/50">Confidence <b className="uppercase">{overall.confidence || "—"}</b></span>
        </div>
      </Panel>

      {report.scoring_transparency?.rows?.length > 0 && (
        <Panel className="p-7" data-testid="ci-scoring-transparency-panel">
          <Kicker className="mb-2">Scoring transparency — reproducible coverage math</Kicker>
          <div className="text-[12px] text-white/45 mb-4 space-y-0.5">
            <div>{report.scoring_transparency.coverage_formula}</div>
            <div>{report.scoring_transparency.score_formula}</div>
          </div>
          <DataTable
            testId="ci-scoring-transparency-table"
            headers={["Dimension", "Weight", "Coverage", "Metrics supported", "High", "Medium", "Low/estimated", "Published score"]}
            rows={report.scoring_transparency.rows.map((r) => [
              <span className="font-medium text-white">{r.name}</span>,
              <span className="tabular-nums">{r.weight}%</span>,
              <span className="tabular-nums">{r.coverage}%</span>,
              <span className="tabular-nums">{r.metrics_supported ?? "—"}/{r.metrics_total ?? "—"}</span>,
              <span className="tabular-nums">{r.high_confidence ?? "—"}</span>,
              <span className="tabular-nums">{r.medium_confidence ?? "—"}</span>,
              <span className="tabular-nums">{r.low_confidence ?? "—"}</span>,
              <span className="tabular-nums text-white">{r.score_display}</span>,
            ])}
          />
          <ul className="mt-4 space-y-1">
            {(report.scoring_transparency.band_rules || []).map((b, i) => (
              <li key={i} className="text-[11px] text-white/40">· {b}</li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel className="p-7">
        <Kicker className="mb-5">Weighted contribution</Kicker>
        <div className="space-y-3">
          {dims.map((d) => (
            <div key={d.key}>
              <div className="flex justify-between text-[12px] mb-1">
                <span className="text-white/70">{d.name}</span>
                <span className="text-white/45 tabular-nums">
                  {d.score ?? "—"} × {d.weight}% = <b className="text-teal-300">{d.weighted ?? "—"}</b>
                </span>
              </div>
              <div className="h-2 rounded-full bg-white/8 overflow-hidden flex">
                <div
                  className={`h-full rounded-full ${d.rag === "green" ? "bg-emerald-400/80" : d.rag === "amber" ? "bg-amber-400/80" : d.rag === "red" ? "bg-red-400/80" : "bg-slate-500/60"}`}
                  style={{ width: `${((d.score ?? 0) * d.weight) / 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-white/35 mt-4">
          RAG bands: Green 75–100 · Amber 50–74 · Red 0–49. Evidence coverage and confidence are reported for transparency; residual data gaps are consolidated in the Evidence &amp; Gaps annexure. Peer percentiles appear only where named-peer evidence exists.
        </div>
      </Panel>
    </div>
  );
};
